import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { BookOpen, Upload, Eye, Download, Loader2, CheckCircle2, Clock, FileText, ClipboardCheck, History } from "lucide-react";

export default function Handbook() {
  const { user } = useAuth();
  const canManage = can(user, ["admin", "regional", "hr"]);

  const [current, setCurrent] = useState(null); // {handbook, acknowledged, acknowledgement}
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [typedName, setTypedName] = useState(user?.name || "");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);

  const [upOpen, setUpOpen] = useState(false);
  const [upForm, setUpForm] = useState({ title: "", notes: "" });
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [viewer, setViewer] = useState(null);
  const [tracking, setTracking] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/handbooks/current"), api.get("/handbooks")])
      .then(([c, v]) => { setCurrent(c.data); setVersions(v.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const hb = current?.handbook;

  const loadTracking = useCallback(() => {
    if (!hb || !canManage) return;
    api.get(`/handbooks/${hb.id}/acknowledgements`).then((r) => setTracking(r.data)).catch(() => setTracking(null));
  }, [hb, canManage]);

  const viewFile = async (id, name) => {
    try {
      const res = await api.get(`/handbooks/${id}/file`, { responseType: "blob" });
      setViewer({ url: URL.createObjectURL(res.data), name: name || "Handbook" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const downloadFile = async (id, name) => {
    const res = await api.get(`/handbooks/${id}/file`, { params: { download: 1 }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = name || "handbook.pdf"; a.click();
    URL.revokeObjectURL(url);
  };

  const sign = async () => {
    if (!agreed) return toast.error("Please check the box to confirm");
    if (typedName.trim().length < 2) return toast.error("Type your full name");
    setSigning(true);
    try {
      await api.post(`/handbooks/${hb.id}/acknowledge`, { typed_name: typedName.trim(), agreed: true });
      toast.success("Handbook acknowledged");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const doUpload = async () => {
    if (!upFile) return toast.error("Choose a PDF");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile);
      await api.post("/handbooks", fd, {
        params: { title: upForm.title, notes: upForm.notes },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("New handbook version published");
      setUpOpen(false); setUpForm({ title: "", notes: "" }); setUpFile(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="handbook-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">People</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Employee Handbook</h1>
          <p className="mt-1 text-sm text-muted-foreground">Read the current handbook and acknowledge it. Your sign-off is saved to your profile.</p>
        </div>
        {canManage && (
          <Button onClick={() => setUpOpen(true)} data-testid="upload-handbook-btn">
            <Upload className="mr-2 h-4 w-4" /> Publish New Version
          </Button>
        )}
      </div>

      {!hb ? (
        <Card className="rounded-none border p-12 text-center">
          <BookOpen className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No handbook has been published yet.</p>
        </Card>
      ) : (
        <Tabs defaultValue="handbook" onValueChange={(v) => { if (v === "tracking") loadTracking(); }}>
          <TabsList className="rounded-none">
            <TabsTrigger value="handbook" data-testid="tab-handbook"><BookOpen className="mr-2 h-4 w-4" /> Handbook</TabsTrigger>
            {canManage && <TabsTrigger value="tracking" data-testid="tab-tracking"><ClipboardCheck className="mr-2 h-4 w-4" /> Sign-offs</TabsTrigger>}
            {canManage && <TabsTrigger value="history" data-testid="tab-history"><History className="mr-2 h-4 w-4" /> Versions</TabsTrigger>}
          </TabsList>

          <TabsContent value="handbook" className="mt-4 space-y-4">
            <Card className="rounded-none border p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-xl font-bold">{hb.title}</h2>
                    <Badge variant="secondary" className="rounded-none">v{hb.version}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">Published {fmtDate(hb.created_at)} by {hb.uploaded_by}</p>
                  {hb.notes && <p className="mt-2 text-sm">{hb.notes}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => viewFile(hb.id, hb.file?.filename)} data-testid="view-handbook-btn"><Eye className="mr-2 h-4 w-4" /> Read</Button>
                  <Button variant="outline" size="sm" onClick={() => downloadFile(hb.id, hb.file?.filename)} data-testid="download-handbook-btn"><Download className="mr-2 h-4 w-4" /> Download</Button>
                </div>
              </div>
            </Card>

            {current.acknowledged ? (
              <Card className="rounded-none border-l-4 border-l-[hsl(var(--success))] p-6" data-testid="ack-confirmed">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" />
                  <div>
                    <div className="font-semibold">You acknowledged this handbook</div>
                    <div className="text-sm text-muted-foreground">
                      Signed as “{current.acknowledgement?.typed_name}” on {fmtDate(current.acknowledgement?.signed_at)}
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="rounded-none border p-6" data-testid="ack-form">
                <h3 className="font-heading text-base font-bold">Acknowledgement</h3>
                <p className="mt-1 text-sm text-muted-foreground">Confirm that you have read and understood the handbook above.</p>
                <div className="mt-4 max-w-md space-y-4">
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="ack-agree" className="mt-0.5" />
                    <span>I have read, understood, and agree to comply with the policies in this Employee Handbook.</span>
                  </label>
                  <div className="space-y-1.5">
                    <Label>Type your full name to sign</Label>
                    <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Full name" data-testid="ack-name" />
                  </div>
                  <Button onClick={sign} disabled={signing || !agreed} data-testid="ack-submit-btn">
                    {signing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign &amp; Acknowledge
                  </Button>
                </div>
              </Card>
            )}
          </TabsContent>

          {canManage && (
            <TabsContent value="tracking" className="mt-4">
              {!tracking ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : (
                <>
                  <div className="mb-4 flex gap-3">
                    <Card className="rounded-none border p-4"><div className="label-caps text-muted-foreground">Signed</div><div className="font-heading text-2xl font-black text-[hsl(var(--success))]">{tracking.signed}</div></Card>
                    <Card className="rounded-none border p-4"><div className="label-caps text-muted-foreground">Pending</div><div className="font-heading text-2xl font-black text-[hsl(var(--warning))]">{tracking.pending}</div></Card>
                    <Card className="rounded-none border p-4"><div className="label-caps text-muted-foreground">Total Users</div><div className="font-heading text-2xl font-black">{tracking.total}</div></Card>
                  </div>
                  <Card className="rounded-none border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                          <TableHead>Status</TableHead><TableHead>Signed On</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tracking.rows.map((r) => (
                          <TableRow key={r.user_id} data-testid={`tracking-row-${r.user_id}`}>
                            <TableCell className="font-medium">{r.name}</TableCell>
                            <TableCell className="text-muted-foreground">{r.email}</TableCell>
                            <TableCell><Badge variant="secondary" className="rounded-none">{ROLE_LABELS[r.role] || r.role}</Badge></TableCell>
                            <TableCell>
                              {r.signed
                                ? <Badge className="rounded-none bg-[hsl(var(--success))] text-white"><CheckCircle2 className="mr-1 h-3 w-3" /> Signed</Badge>
                                : <Badge className="rounded-none bg-[hsl(var(--warning))] text-black"><Clock className="mr-1 h-3 w-3" /> Pending</Badge>}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{r.signed_at ? fmtDate(r.signed_at) : "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </>
              )}
            </TabsContent>
          )}

          {canManage && (
            <TabsContent value="history" className="mt-4">
              <Card className="rounded-none border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead><TableHead>Title</TableHead><TableHead>Published</TableHead>
                      <TableHead>By</TableHead><TableHead className="text-right">File</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versions.map((v) => (
                      <TableRow key={v.id} data-testid={`version-row-${v.id}`}>
                        <TableCell><Badge variant="secondary" className="rounded-none">v{v.version}</Badge></TableCell>
                        <TableCell className="font-medium">{v.title}</TableCell>
                        <TableCell className="text-muted-foreground">{fmtDate(v.created_at)}</TableCell>
                        <TableCell className="text-muted-foreground">{v.uploaded_by}</TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => viewFile(v.id, v.file?.filename)}><Eye className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => downloadFile(v.id, v.file?.filename)}><Download className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Upload dialog */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent data-testid="upload-handbook-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Publish New Handbook Version</DialogTitle>
            <DialogDescription>Uploading creates a new version. All users will be asked to acknowledge it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={upForm.title} onChange={(e) => setUpForm((f) => ({ ...f, title: e.target.value }))} placeholder="Employee Handbook 2026" data-testid="handbook-title" />
            </div>
            <div className="space-y-1.5">
              <Label>What changed (optional)</Label>
              <Input value={upForm.notes} onChange={(e) => setUpForm((f) => ({ ...f, notes: e.target.value }))} data-testid="handbook-notes" />
            </div>
            <div className="space-y-1.5">
              <Label>Handbook PDF</Label>
              <label className="flex cursor-pointer items-center gap-2 border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary">
                <Upload className="h-4 w-4" />
                {upFile ? upFile.name : "Choose PDF"}
                <input type="file" accept=".pdf" className="hidden" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="handbook-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpOpen(false)}>Cancel</Button>
            <Button onClick={doUpload} disabled={uploading} data-testid="save-handbook-btn">
              {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) { if (viewer) URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="handbook-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><FileText className="h-5 w-5" /> {viewer?.name}</DialogTitle>
          </DialogHeader>
          {viewer && <iframe title="handbook" src={viewer.url} className="h-[70vh] w-full border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
